import ActionToggle from 'live/components/ui/action-toggle'
import Tooltips from 'live/components/ui/tooltip'
import i18n from 'live/services/i18n'
import _ from 'lodash'
import { Fieldset } from 'plugin-liverig/shared/asset/components/Fieldset'
import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { UnitConfig } from '../../types/ConstantConfigType'
import { PresetDataType } from '../ModelsConfigTypes'
import { getPhysicalModelParameters } from '../ModelsSettings'
import { ParametersTable, ParametersTableDataType } from './ParametersTable'

type PresetSheetProps = {
    unitSet: UnitConfig
    changed: boolean
    onChange: React.Dispatch<React.SetStateAction<ParametersTableDataType>>
    setCanEdit: (change: boolean) => void
    onSave(localPreset: PresetDataType, globalPreset: PresetDataType)
    onCancel()
    physicalModelParameters: PresetDataType
    globalPhysicalModelParameters: PresetDataType
    editTable?: boolean
}

type dataValuesType = {
    label: string
    unit: string
    defaultValue: string | unknown
    value: string | unknown
    typeParam: string
}

export default function PresetSheet({
    unitSet,
    changed,
    onChange,
    setCanEdit,
    onSave,
    onCancel,
    physicalModelParameters,
    globalPhysicalModelParameters,
    editTable
}: PresetSheetProps): React.ReactElement {
    const [tableData, setTableData] = useState({} as ParametersTableDataType)
    const [advancedTable, setAdvancedTable] = useState({} as ParametersTableDataType)

    const [showAdvancedTable, setShowAdvancedTable] = useState(true)
    useEffect(() => {
        const dataParamters = getPhysicalModelParameters(unitSet)

        const parsedTableData: ParametersTableDataType = {
            parameter: {
                label: i18n('Parameter'),
                index: 1,
                data: [],
                viewRender: value => {
                    return i18n(dataParamters[value]?.label)
                }
            },
            unit: {
                index: 2,
                data: [],
                label: (
                    <Tooltips
                        position="top"
                        content={i18n(
                            'The default units are defined in the Rig State Configurations. Click to go to default units configurations.'
                        )}
                        hide={false}
                    >
                        <a
                            href="/#/config/rig-state-settings"
                            target="blank"
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                            }}
                        >
                            <span
                                style={{
                                    marginRight: '8px'
                                }}
                            >
                                {i18n('Unit')}
                            </span>
                            <i className="far fa-info-circle" />
                        </a>
                    </Tooltips>
                )
            },
            'Default value': {
                label: (
                    <Tooltips
                        position="top"
                        content={i18n(
                            'The default values are defined in the Rig State Configurations. ' +
                                'They are not applied to this Rig, unless you leave the fields blank. ' +
                                'Click to go to default values configurations.'
                        )}
                        hide={false}
                    >
                        <a
                            href="/#/config/rig-state-settings"
                            target="blank"
                            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                        >
                            <span>{i18n('Default value')}</span>
                            <i className="far fa-info-circle" />
                        </a>
                    </Tooltips>
                ),
                index: 3,
                data: [],
                customClassnames: []
            },
            'specified value': {
                label: (
                    <div style={{ display: 'flex', justifyContent: 'start', alignItems: 'center' }}>
                        <Tooltips
                            position="top"
                            content={i18n(
                                'The specified values are applied to the calculations of this rig. ' +
                                    'If you leave any field empty, the default value will be used.'
                            )}
                            hide={false}
                        >
                            <div>
                                <span>{i18n('specified value')}</span>
                                <i
                                    style={{ color: 'var(--blue-500)', paddingLeft: '8px' }}
                                    className="far fa-info-circle"
                                />
                            </div>
                        </Tooltips>
                    </div>
                ),
                index: 4,
                edit: true,
                type: 'number',
                min: 0,
                data: [],
                customClassnames: []
            }
        }

        const parsedAdvancedTableData: ParametersTableDataType = {
            'Advanced options': {
                label: i18n('Advanced Options'),
                index: 1,
                data: [],
                customHeaderClass: 'advanced-line',
                customClassnames: [],
                viewRender: value => {
                    return i18n(dataParamters[value]?.label || value)
                }
            },
            unit: {
                hideHeader: true,
                index: 2,
                data: [],
                customHeaderClass: 'advanced-line',
                customClassnames: []
            },
            'Default value': {
                hideHeader: true,
                index: 3,
                data: [],
                customHeaderClass: 'advanced-line',
                customClassnames: [],
                viewRender: value => {
                    return i18n(value)
                }
            },
            'specified value': {
                customHeaderClass: 'advanced-line advanced-value',
                index: 4,

                label: (
                    <div onClick={() => setShowAdvancedTable(prev => !prev)}>
                        <i className={`fas fa-chevron-${showAdvancedTable ? 'up' : 'down'}`}></i>
                    </div>
                ),
                edit: true,
                inputRender: (value, onChange, lineIndex, className) => {
                    return (
                        <ActionToggle
                            className={className}
                            id={`${value}@${lineIndex}`}
                            onChange={() => onChange(value === 'yes' ? 'no' : 'yes')}
                            active={value === 'yes'}
                        />
                    )
                },
                viewRender: value => {
                    return i18n(value)
                },
                data: [],
                customClassnames: []
            }
        }

        const { enableSSAFiltering, hookLoadWithoutBlockWeight, ...presetParameters } = physicalModelParameters

        Object.keys(presetParameters || {}).forEach(lineName => {
            const globalParameterValue = globalPhysicalModelParameters?.[lineName]
            const parameterValue = presetParameters[lineName]

            const valueIEqual = globalParameterValue?.value === parameterValue?.value
            const valueChanged = !valueIEqual && globalParameterValue?.timestamp > parameterValue?.timestamp

            parsedTableData.parameter?.data.push(lineName)
            parsedTableData.unit?.data.push(dataParamters[lineName]?.unit)
            parsedTableData['Default value']?.data.push(
                _.isNumber(globalParameterValue?.value) ? globalParameterValue?.value : '-'
            )
            parsedTableData['specified value']?.data.push(parameterValue?.value)

            parsedTableData['Default value']?.customClassnames.push(valueChanged ? 'changed' : '')
            parsedTableData['specified value']?.customClassnames.push(valueIEqual ? 'unchanged' : '')
        })

        parsedAdvancedTableData['Advanced options']?.data.push('enableSSAFiltering')
        parsedAdvancedTableData['Default value']?.data.push(
            globalPhysicalModelParameters.enableSSAFiltering?.value ? 'yes' : 'no'
        )
        parsedAdvancedTableData['specified value']?.data.push(enableSSAFiltering?.value ? 'yes' : 'no')

        const changedEnableSSAFiltering =
            globalPhysicalModelParameters.enableSSAFiltering?.timestamp > enableSSAFiltering?.timestamp ? 'changed' : ''

        const unChangedEnableSSAFiltering =
            enableSSAFiltering?.timestamp < globalPhysicalModelParameters.enableSSAFiltering?.timestamp
                ? 'unchanged'
                : ''

        parsedAdvancedTableData['Default value']?.customClassnames.push(`advanced-line ${changedEnableSSAFiltering}`)
        parsedAdvancedTableData['specified value']?.customClassnames.push(
            `advanced-line ${unChangedEnableSSAFiltering}`
        )

        parsedAdvancedTableData['Advanced options']?.data.push('hookLoadWithoutBlockWeight')
        parsedAdvancedTableData.unit?.data.push('')
        parsedAdvancedTableData['Default value']?.data.push(
            globalPhysicalModelParameters.hookLoadWithoutBlockWeight?.value ? 'yes' : 'no'
        )
        parsedAdvancedTableData['specified value']?.data.push(hookLoadWithoutBlockWeight?.value ? 'yes' : 'no')

        parsedAdvancedTableData['Advanced options']?.customClassnames.push('advanced-line')
        parsedAdvancedTableData.unit?.customClassnames.push('advanced-line')

        const changedHookLoadWithoutBlockWeight =
            hookLoadWithoutBlockWeight?.timestamp < globalPhysicalModelParameters.hookLoadWithoutBlockWeight?.timestamp
                ? 'changed'
                : ''

        const unChangedHookLoadWithoutBlockWeight =
            hookLoadWithoutBlockWeight?.timestamp < globalPhysicalModelParameters.hookLoadWithoutBlockWeight?.timestamp
                ? 'unchanged'
                : ''

        parsedAdvancedTableData['Default value']?.customClassnames.push(
            `advanced-line ${changedHookLoadWithoutBlockWeight}`
        )
        parsedAdvancedTableData['specified value']?.customClassnames.push(
            `advanced-line ${unChangedHookLoadWithoutBlockWeight}`
        )

        parsedAdvancedTableData['Default value']?.customClassnames.push()
        parsedAdvancedTableData['specified value']?.customClassnames.push()

        setTableData(parsedTableData)
        setAdvancedTable(parsedAdvancedTableData)
    }, [physicalModelParameters, showAdvancedTable])

    const handleChange = (newTableData: ParametersTableDataType) => {
        const parsedPreset = {} as PresetDataType

        newTableData.parameter?.data?.forEach((parameter: string, i) => {
            const parameterValue = newTableData['specified value'].data[i]
            parsedPreset[parameter] = {
                value: parameterValue,
                timestamp: physicalModelParameters[parameter]?.timestamp
            }
            if (!_.isEmpty(physicalModelParameters) && physicalModelParameters[parameter] !== parameterValue) {
            }
        })

        setTableData(newTableData)
        onChange(prev => {
            return { ...prev, ...parsedPreset }
        })
    }

    const handleAdvancedChange = (newTableData: ParametersTableDataType) => {
        const parsedPreset = {} as PresetDataType

        newTableData['Advanced options']?.data?.forEach((parameter: string, i) => {
            const parameterValue = newTableData['specified value'].data[i] === 'yes'
            parsedPreset[parameter] = { value: parameterValue, timestamp: physicalModelParameters[parameter].timestamp }
            if (!_.isEmpty(physicalModelParameters) && physicalModelParameters[parameter] !== parameterValue) {
                setCanEdit(true)
            }
        })

        setAdvancedTable(newTableData)
        onChange((prev: PresetDataType) => {
            return { ...prev, ...parsedPreset }
        })
    }

    return (
        <StyledCardContainer
            title={i18n('Calculation parameters')}
            hideRequired
            onSubmit={resp => {
                onSave(physicalModelParameters, globalPhysicalModelParameters)
            }}
            onCancel={() => {
                onCancel()
                setCanEdit(false)
            }}
            toggleEdit={() => setCanEdit(true)}
            canEdit={true}
            editing={editTable}
        >
            <>
                <ParametersTable
                    height={600}
                    readOnly={!editTable}
                    setReadOnly={readonly => setCanEdit(!readonly)}
                    tableData={tableData}
                    onChange={handleChange}
                />
                <ParametersTable
                    readOnly={!editTable}
                    setReadOnly={readonly => setCanEdit(!readonly)}
                    tableData={advancedTable}
                    onChange={handleAdvancedChange}
                    hideData={!showAdvancedTable}
                />
            </>
        </StyledCardContainer>
    )
}

const StyledCardContainer = styled(Fieldset)`
    width: 100%;
    padding: 0;
    margin: 0;

    box-shadow: ${({ theme }) => theme['light-pink-borders']} 0px 0px 5px 0px;
    //height: 40vh;
    > header {
        > div:first-of-type {
            text-transform: uppercase;
            box-shadow: 0;
        }
    }

    &.form-group {
        margin: unset;
    }

    fieldset {
        //max-height: 70rem;

        > div:first-of-type {
            height: 594px;
        }
    }

    .capitalize {
        text-transform: capitalize;
    }

    .table-col-header {
        background: var(--gray-300) !important;
        height: 32px !important;
        text-transform: Uppercase;
        color: var(--gray-800);
        border: 1px solid;
        padding: 16px;
        border-color: var(--gray-300);
    }

    .advanced-line {
        font-weight: 400;
        background: var(--gray-100) !important;
        height: 32px !important;
        text-transform: capitalize !important;
        color: var(--gray-600);
        border: 0px;
        padding: 8px !important;
    }

    .advanced-value {
        border-right: 1px solid;
        border-color: var(--gray-300);
        div {
            width: 100%;
            display: flex;
            justify-content: flex-end !important;
        }
    }

    .table-col-header-advanced_options {
        border-left: 1px solid;
        border-color: var(--gray-300);
        width: 232px !important;
    }

    .editing {
        border: 2px solid !important;
        border-color: var(--blue-500) !important;
    }

    .table-col {
        background: var(--gray-200) !important;
        height: 32px !important;
        padding: 0px 12px !important;
        color: var(--gray-600) !important;
        border: 1px solid;
        border-color: var(--gray-300);
    }

    .table-col-header-parameter {
        width: 232px !important;
    }

    .table-col-parameter {
        font-size: 13px;
        margin: 12px;
        //width:232px !important;
        text-transform: Capitalize !important;
    }

    .table-col-default_value {
        text-align: center;
    }

    .changed {
        color: var(--red-500) !important;
    }

    .unchanged {
        input {
            color: var(--gray-500) !important;
        }
    }

    .table-col-specified_value {
        background: var(--gray-0) !important;
        //width:50% !important;
        text-transform: Capitalize;

        padding: 0px 8px;
        input {
            color: var(--gray-800);
            border: none;
            width: 100%;
        }
    }

    th {
        background: ${({ theme }) => theme['gray-dark-bg']};
    }
    .table-col-unit {
        width: 100px !important;
        height: 32px !important;
    }

    .table-col-header-default_value {
        color: var(--blue-500);
        width: 130px !important;
        padding: 0px 8px;
    }
`
